import React, { useState, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@shadcn/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@shadcn/components/ui/command';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { ChevronsUpDown, Check } from 'lucide-react';
import { groupFieldsByEntity } from '../fieldUtils';

const TYPE_BADGE_COLORS = {
  string: 'bg-blue-100 text-blue-700',
  number: 'bg-green-100 text-green-700',
  boolean: 'bg-purple-100 text-purple-700',
  date: 'bg-orange-100 text-orange-700',
  array: 'bg-pink-100 text-pink-700',
  object: 'bg-gray-100 text-gray-600',
};

const FieldPicker = ({ value, onChange, fields = [] }) => {
  const [open, setOpen] = useState(false);

  const groupedFields = useMemo(() => groupFieldsByEntity(fields), [fields]);

  // If no fields are loaded, fall back to plain text input
  if (!fields.length) {
    return (
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value, null)}
        placeholder="field.path"
        className="flex-1 h-8 text-xs min-w-0"
      />
    );
  }

  const selectedField = fields.find((f) => f.path === value);
  const displayLabel = selectedField
    ? selectedField.label
    : value || 'Select field...';

  const handleSelect = (field) => {
    onChange(field.path, field.type);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex-1 h-8 text-xs justify-between min-w-0 font-normal"
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search fields..." className="h-9" />
          <CommandList>
            <CommandEmpty>No fields found.</CommandEmpty>
            {Object.entries(groupedFields).map(([group, groupFields]) => (
              <CommandGroup key={group} heading={group}>
                {groupFields.map((field) => (
                  <CommandItem
                    key={field.path}
                    value={`${field.label} ${field.path}`}
                    onSelect={() => handleSelect(field)}
                    className="text-xs"
                  >
                    <Check
                      className={`mr-2 h-3 w-3 shrink-0 ${value === field.path ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate">{field.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono truncate">
                        {field.path}
                      </span>
                    </div>
                    <span
                      className={`ml-2 text-[10px] px-1.5 py-0.5 rounded shrink-0 ${TYPE_BADGE_COLORS[field.type] || TYPE_BADGE_COLORS.object}`}
                    >
                      {field.type}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default FieldPicker;
